import { FlattenedOptJobAndQueueItem, OptJobStatus, OptJobType } from 'api/optJobs/optJobs.api'
import { saveAs } from 'file-saver'
import i18n from 'i18next'
import Datetime from 'utils/datetime/datetime'
import getLogger from 'utils/log/log'

const log = getLogger('Optimizations.helper')

export function optJobStatusTranslationMapping(status: OptJobStatus): string | undefined {
  if (status === null || status === undefined) {
    return i18n.t('No status found')
  }
  const map: { [key: string]: string } = {
    Created: i18n.t('Fetching optimization data'),
    Waiting: i18n.t('Fetching optimization data'),
    Queued: i18n.t('Preparing optimization'),
    Running: i18n.t('Optimizing'),
    Failed: i18n.t('Failed'),
    Unsolvable: i18n.t('Unsolvable'),
    Finished: i18n.t('Finished'),
  }
  return map[status]
}

//Show more pretty names for jobs that have finished in Review Optimizations, in future we might want to think about what the name means (what priorities does it have etc)
export function optJobTypeTranslationMapping(type: OptJobType): string | undefined {
  if (type === null || type === undefined) {
    return type
  }
  const map: Record<OptJobType, string> = {
    regular: i18n.t('Production plan'),
    regular_wip: i18n.t('Draft production plan'),
    followup_with_deviations: i18n.t('Follow-up with deviations'),
    followup_without_deviations: i18n.t('Follow-up without deviations'),
    followup_regular: i18n.t('Follow-up'),
    measvalues_calculations: i18n.t('Measurement values calculation'),
    marginal_cost_calculation: i18n.t('Marginal cost calculation'),
    sandbox: i18n.t('Sandbox optimization'),
    real_time: i18n.t('Real time'),
    real_time_measvalues_calculations: i18n.t('Real time measurement values calculation'),
    fuel_plan: i18n.t('Scenario'),
    electricity_price_scenario: i18n.t('Electricity price scenario'),
    intraday_plan: i18n.t('Intraday plan'),
    settlement: i18n.t('Settlements'),
  }
  return map[type]
}

// Distinct pill style per opt job type, shown next to the type in the review dropdown -
// matches the bg/text/border pill pattern used for priority tags in DigitalTwinCollectedSettingsView.
export function getOptJobTypeTagStyle(type: OptJobType): string {
  const map: Record<OptJobType, string> = {
    regular: 'bg-primary-1 text-primary-6 border-primary-2',
    regular_wip: 'bg-info-1 text-info-6 border-info-2',
    // Same green family for all follow-up types, shaded lightest to darkest so they stay distinguishable.
    followup_regular: 'bg-success-3 text-success-7 border-success-2',
    followup_without_deviations: 'bg-success-2 text-success-7 border-success-3',
    followup_with_deviations: 'bg-success-1 text-success-6 border-success-4',
    measvalues_calculations: 'bg-grey-2 text-grey-7 border-grey-4',
    marginal_cost_calculation: 'bg-info-1 text-info-6 border-info-2',
    sandbox: 'bg-warning-1 text-warning-6 border-warning-3',
    real_time: 'bg-danger-1 text-danger-6 border-danger-2',
    real_time_measvalues_calculations: 'bg-grey-2 text-grey-7 border-grey-4',
    fuel_plan: 'bg-warning-1 text-warning-6 border-warning-3',
    electricity_price_scenario: 'bg-info-1 text-info-6 border-info-2',
    intraday_plan: 'bg-danger-1 text-danger-6 border-danger-2',
    settlement: 'bg-[#E9DDF6] text-[#4B1D68] border-[#B58AD4]',
  }
  return map[type] ?? 'bg-grey-2 text-grey-7 border-grey-4'
}

// A flattened opt job/queue item tagged with the opt job type it was fetched for, since the
// backend response for `opt_job/queue_items_status` doesn't include it and the review list now
// blends several opt job types together.
export type OptJobAndQueueItemWithType = FlattenedOptJobAndQueueItem & { opt_job_type: OptJobType }

// Merges per-type opt job/queue item lists into one, newest first, so the review dropdown can show
// production plan, follow-up (incl. deviations) and settlement optimizations side by side. `cap`
// truncates the merged, sorted list so the dropdown shows the N latest overall, regardless of type.
export function mergeOptJobAndQueueItemsByType(
  itemsByType: { optJobType: OptJobType; items: FlattenedOptJobAndQueueItem[] }[],
  cap?: number
): OptJobAndQueueItemWithType[] {
  const tagged = itemsByType.flatMap(({ optJobType, items }) =>
    items.map((item) => ({ ...item, opt_job_type: optJobType }))
  )
  const sorted = tagged.sort((a, b) =>
    Datetime.compare(Datetime.toISOString(b.opt_job_created_at), Datetime.toISOString(a.opt_job_created_at))
  )
  return cap === undefined ? sorted : sorted.slice(0, cap)
}

export function getMostRecentFinishedOptJobAndQueueItem(
  selected: OptJobAndQueueItemWithType | null,
  list: OptJobAndQueueItemWithType[] | undefined
): OptJobAndQueueItemWithType | null {
  if (!selected || !list) {
    return null
  }

  const indexOfSelected = selected && list ? list.findIndex((item) => optJobAndQueueItemEqual(item, selected)) : -1
  const mostRecentFinished = list?.find(
    (item, index) =>
      index > indexOfSelected &&
      item.opt_job_status === 'Finished' &&
      item.opt_job_type === selected.opt_job_type &&
      item.queue_item_subtype === selected.queue_item_subtype
  )
  return mostRecentFinished || null
}

function optJobAndQueueItemEqual(a: OptJobAndQueueItemWithType | null, b: OptJobAndQueueItemWithType | null) {
  if (a === null || b === null) {
    return false
  }

  if (a === b) {
    return true
  }

  return a.opt_job_id === b.opt_job_id && a.queue_item_id === b.queue_item_id
}

export function downloadAsJSON(content: unknown, filename: string): void {
  try {
    const jsonString = JSON.stringify(content)
    const blob = new Blob([jsonString], { type: 'application/json' })
    saveAs(blob, `${filename}.json`)
  } catch (error) {
    log.error('Failed to download JSON:', error)
  }
}

const DEFAULT_RUN_PERIOD_DAYS = 7
// Mirrors the period StartOptimizationButton requests for a fuel plan: start of day to 30 days later minus 1 hour.
const FUEL_PLAN_RUN_PERIOD_DAYS = 30

/**
 * The period an opt job optimized over, derived from when it was created. The review endpoint does not return
 * the job's own start and end time, so this assumes the period each type is started with.
 */
export function getOptJobRunPeriod(
  optJobType: OptJobType | undefined,
  createdAt: ISODateTime
): { start: ISODateTime; end: ISODateTime } {
  if (optJobType === 'fuel_plan') {
    const start = Datetime.getStartOfDay(createdAt)
    return { start, end: Datetime.subtractHours(Datetime.addDays(start, FUEL_PLAN_RUN_PERIOD_DAYS), 1) }
  }

  const start = Datetime.ISODatetimeTo00Minutes(createdAt)
  return { start, end: Datetime.addDays(start, DEFAULT_RUN_PERIOD_DAYS) }
}
